'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import TSLogo from '@/components/global/ui/TSLogo';
import type { PageHomeSchema } from '@/sanity/pages/PageHomeTemplate';

export type TrustDataSectionData = Pick<PageHomeSchema, 'trustDataText' | 'trustDataCTA'>;

const EASE = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: '-50px' } as const;
const REVEAL_LOGO = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_TEXT = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_CTA = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const LOGO_TRANSITION = { duration: 0.6, ease: [...EASE] };
const TEXT_TRANSITION = { duration: 0.7, ease: [...EASE], delay: 0.1 };
const FALLBACK_P_TRANSITION = { duration: 0.7, ease: [...EASE], delay: 0.18 };
const CTA_TRANSITION = { duration: 0.7, ease: [...EASE], delay: 0.26 };

function TrustDataSectionDesktop({ data }: { data: TrustDataSectionData }) {
  const { trustDataText, trustDataCTA } = data;

  return (
    <section className="hidden lg:flex relative w-full flex-col items-center z-10">
      <div className="w-[180rem] flex flex-col items-center bg-white py-[7rem]">
        <div className="flex flex-col items-center text-center gap-[4rem] w-[min(61rem,100%)]">
          <motion.div className="flex" {...REVEAL_LOGO} transition={LOGO_TRANSITION}>
            <TSLogo fill="#f50" width="5.625rem" height="5.875rem" />
          </motion.div>

          {trustDataText ? (
            <motion.div
              className="richtext richtext-center prose-headings:text-black prose-headings:font-normal prose-headings:text-[8rem] prose-headings:leading-none prose-headings:tracking-normal prose-p:text-black prose-p:text-[2.25rem] prose-headings:mb-0 flex flex-col gap-[4rem] prose-p:m-0 prose-headings:[text-box-trim:trim-both] prose-headings:[text-box-edge:cap_alphabetic] prose-p:[text-box-trim:trim-both] prose-p:[text-box-edge:cap_alphabetic]"
              {...REVEAL_TEXT}
              transition={TEXT_TRANSITION}
            >
              <PortableText
                value={trustDataText}
                components={richTextComponent}
              />
            </motion.div>
          ) : (
            <motion.div {...REVEAL_TEXT} transition={TEXT_TRANSITION}>
              <h2 className="text-black font-normal text-[8rem] leading-none tracking-normal m-0 [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">
                {`Don't trust us.`}
                <br />
                Trust your data.
              </h2>
            </motion.div>
          )}

          {!trustDataText && (
            <motion.p
              className="text-black text-[2.25rem] leading-[1.5] m-0 [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]"
              {...REVEAL_CTA}
              transition={FALLBACK_P_TRANSITION}
            >
              Stop the endless search for the perfect journal. TradeStats is the
              last and best journal you&apos;ll ever need—the tool built that
              turns your history into a permanent edge.
            </motion.p>
          )}

          {trustDataCTA && (
            <motion.div
              className="w-fit"
              {...REVEAL_CTA}
              transition={CTA_TRANSITION}
            >
              <CTAButton data={trustDataCTA} />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

function TrustDataSectionMobile({ data }: { data: TrustDataSectionData }) {
  const { trustDataText, trustDataCTA } = data;

  return (
    <section className="lg:hidden relative w-full flex flex-col items-center z-10 px-[3rem]">
      <div className="w-full flex flex-col items-center bg-white p-[4rem] border border-white/20">
        <div className="flex flex-col items-center text-center gap-[4rem] w-[min(61rem,100%)]">
          <div className="flex">
            <TSLogo fill="#f50" width="5.625rem" height="5.875rem" />
          </div>

          {trustDataText ? (
            <div className="richtext richtext-center prose-headings:text-black prose-headings:font-normal prose-headings:text-[5rem] prose-headings:leading-none prose-headings:tracking-normal prose-p:text-black prose-p:text-[2.25rem] prose-headings:mb-0 flex flex-col gap-[4rem] prose-p:m-0 prose-headings:[text-box-trim:trim-both] prose-headings:[text-box-edge:cap_alphabetic] prose-p:[text-box-trim:trim-both] prose-p:[text-box-edge:cap_alphabetic]">
              <PortableText
                value={trustDataText}
                components={richTextComponent}
              />
            </div>
          ) : (
            <div>
              <h2 className="text-black font-normal text-[5rem] leading-none tracking-normal m-0 [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">
                {`Don't trust us.`}
                <br />
                Trust your data.
              </h2>
            </div>
          )}

          {!trustDataText && (
            <p className="text-black text-[2.25rem] leading-[1.5] m-0 [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">
              Stop the endless search for the perfect journal. TradeStats is the
              last and best journal you&apos;ll ever need—the tool built that
              turns your history into a permanent edge.
            </p>
          )}

          {trustDataCTA && (
            <div className="w-full">
              <CTAButton data={trustDataCTA} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function TrustDataSection({ data }: { data: TrustDataSectionData }) {
  return (
    <>
      <TrustDataSectionDesktop data={data} />
      <TrustDataSectionMobile data={data} />
    </>
  );
}
